import {
    GQLAdditionalParticipantFragment,
    GQLClientContact,
    GQLDiagnosis,
    GQLEditorSegment,
    GQLGender,
    GQLModality,
    GQLOrganizationRegion,
    GQLPronoun,
    GQLSessionDocumentFragment,
    GQLSessionNoteType,
    GQLSessionState,
    GQLTherapySessionAnonymousParticipantsType,
} from '@upheal/graphql/definitions/schema.generated';
import { Values } from './utils/utility-types';
import { PhoneNumber } from '@upheal/utils/phoneNumberUtils';

export type PagedResponse<T> = {
    items: T[];
    totalCount: number;
    offset: number;
    pageSize: number;
};

export enum UserRole {
    Admin = 'Admin',
    Client = 'Client',
    HealthcareProvider = 'HealthcareProvider',
    OrgAdmin = 'OrgAdmin',
    VideoCallGuest = 'VideoCallGuest',
}

export const RoleNames = {
    [UserRole.Admin]: 'Admin',
    [UserRole.Client]: 'Client',
    [UserRole.HealthcareProvider]: 'Provider',
    [UserRole.OrgAdmin]: 'Admin',
    [UserRole.VideoCallGuest]: 'Video Call Guest',
};

export enum UserStatus {
    Pending = 'Pending',
    Active = 'Active',
    Inactive = 'Inactive',
    Registration = 'Registration',
}

export enum ConsentStatus {
    Pending = 'Pending',
    Requested = 'Requested',
    Accepted = 'Accepted',
}

export const Gender = GQLGender;
export type Gender = GQLGender;
export const Pronoun = GQLPronoun;
export type Pronoun = GQLPronoun;

export const PronounLabels: Record<Pronoun, string> = {
    [Pronoun.FeminineHer]: 'She/her',
    [Pronoun.MasculineHim]: 'He/him',
    [Pronoun.NeutralThem]: 'They/them',
    // [Pronoun.NeutralZir]: 'Ze/zir',
    // [Pronoun.NeutralHir]: 'Ze/hir',
} as const;

export interface IAddress {
    street?: string | null;
    city?: string | null;
    stateOrProvince?: string | null; // based on Google PlaceService administrative_area_level_1 field
    zipCode?: string | null;
    countryCode?: string | null;
    googlePlaceId?: string | null; // allows requesting more details through Google PlaceService
}

export type ConsentForDataProcessingConfirmed =
    | 'Confirmed'
    | 'Pending' // Users created after the new consent was introduced
    | 'PendingExisting' // User created before the new consent was introduced
    | 'Skipped'; // Org wide settings takes precedence

export type ConsentForTelehealthRecordingStorageConfirmed =
    | 'Confirmed'
    | 'Pending';

export const LoginMethod = {
    google: 'google',
    email: 'email',
} as const;

export type LoginMethod = Values<typeof LoginMethod>;

export type User = {
    firstName: string;
    lastName: string;
    email?: string | null;
    id: string;
    locale: string;
    birthDate?: string | null; // iso date
    gender?: Gender | null;
    pronoun?: Pronoun | null;
    phone?: string | null;
    workPhone?: string;
    role: UserRole;
    status: UserStatus;
    isActive: boolean;
    isDemo?: boolean | null;
    referrer?: string;
    organizationIds?: string[];
    organizations?: Organization[];
    hmac?: string;
    privateRoomLink?: string;
    sessionsCount?: number | null;
    lastSessionTimestamp?: string | null;
    defaultSessionType?: TherapySessionType | null;
    referralCode?: string;
    integrations?: {
        calendar?: {
            connected?: boolean | null;
        };
        zoom?: {
            connected?: boolean | null;
        };
    };
    address?: IAddress | null;
    nationalProviderIdentifier?: string | null;
    therapyLicense?: string | null;
    encryptionKey?: string & { length: 32 };
    googleMapsApiKey?: string;
    sharedClientsModeEnabled?: boolean;
    orgAdminSharedClientsModeEnabled?: boolean;
    consentForDataProcessingConfirmed: ConsentForDataProcessingConfirmed;
    consentForTelehealthRecordingStorageConfirmed: ConsentForTelehealthRecordingStorageConfirmed;
    availableSessionTypes?: TherapySessionType[];
    hasVoiceId: boolean;
    registeredAt: string; // "2024-10-12T21:40:50.419"
    login?: {
        method: LoginMethod;
        profilePictureUrl?: string;
    };
};

export enum InviteLinkStatus {
    Registered = 'Registered',
    Created = 'Created',
    Used = 'Used',
}

export type InviteInfo = {
    firstName: string;
    lastName: string;
    email: string;
    userId: string;
    role: UserRole;
    organizationId?: string;
    isNewOrganization: boolean;
    parameters?: {
        phoneNumber?: string;
        phoneCountryCode?: string;
        workPhoneNumber?: string;
        workPhoneCountryCode?: string;
        organizationName?: string;
    };
    status: InviteLinkStatus;
    countryCode?: string;
    ssoEnabled?: boolean;
};

export type AuthResponse = {
    accessToken: string;
    // @deprecated
    expiresInSeconds: number;
    accessTokenExpiresInSeconds: number;
    refreshTokenExpiresInSeconds: number;
    verificationId?: string | null;
    redactedPhone?: string | null;
};

export type SSOAuthResponse = AuthResponse & {
    firstTimeSignIn: boolean;
    domain: string;
};

export type Credentials = {
    username: string;
    password: string;
    rememberMe: boolean;
};

export type VerificationCredentials = {
    verificationId: string;
    verificationCode: string;
    rememberMe: boolean;
};

export const GoogleSignInCallbackType = {
    login: 'LOGIN',
    ssoSignin: 'SSO_SIGNIN',
} as const;

export type GoogleSignInCallbackType = Values<typeof GoogleSignInCallbackType>;

export type SSOCredentials = {
    code: string;
    state: string;
    rememberMe: boolean;
    callbackType?: GoogleSignInCallbackType;
};

export type KeepRecordingValue = {
    audio: boolean;
    video: boolean;
    none: boolean;
};

export const TherapySessionType = GQLSessionNoteType;
export type TherapySessionType = GQLSessionNoteType;

// @deprecated by RecordingContentType and contracts
export enum ParticipantsType {
    Individual = 'Individual',
    DictatedNotes = 'DictatedNotes',
    Couple = 'Couple',
}

export enum InputFileSource {
    UphealSession = 'UphealSession',
    ZoomSession = 'ZoomSession',
    ManualUpload = 'ManualUpload',
    AudioRecorder = 'AudioRecorder',
    ExtensionRecorder = 'ExtensionRecorder',
    VirtualSessionRecorder = 'VirtualSessionRecorder',
    TextToNote = 'TextToNote',
    ChartImport = 'ChartImport',
}

export enum TherapySessionFormat {
    InPerson = 'InPerson',
    Online = 'Online',
}

export const DefaultClientLocation = {
    Home: 'Home',
    Office: 'Office',
} as const;

export enum RecordingContentType {
    DictatedNotes = 'DictatedNotes',
    Recording = 'Recording',
    TextToNote = 'TextToNote',
}

export enum TherapySessionErrorCode {
    LowContentTranscript = 'low-content-transcript',
    RecordingDurationExceeded = 'recording-duration-exceeded',
}

export type TherapySession = {
    anonymousParticipantsType?: GQLTherapySessionAnonymousParticipantsType;
    counter?: number;
    date: string;
    contractId?: string;
    sessionFormat?: TherapySessionFormat;
    clientLocation?: string;
    duration?: number;
    plannedDuration?: number;
    recordingDuration?: number;
    recordingDate?: string;
    lastProcessingDateUtc?: string;
    manualUpload?: boolean;
    sessionId: string;
    sessionName?: string;
    sessionSummary?: string;
    cptCodes?: string[];
    modality?: string[];
    modalities?: Array<GQLModality>;
    diagnoses?: Array<GQLDiagnosis>;
    state: GQLSessionState;
    roomUrl: string;
    progress?: SessionProcessingProgressType;
    entities: NamedEntityPreviewType[];
    keepRecording?: KeepRecordingValue;
    isTranscriptDeleted?: boolean;
    sessionType?: TherapySessionType;
    // @deprecated by recordingContentType
    participantsType?: ParticipantsType;
    recordingContentType?: RecordingContentType;
    inputFileSource: InputFileSource;
    seen?: boolean;
    isAccessible?: boolean; // if undefined, then it's accessible. It has to be strictly set to false to be inaccessible
    signOffDateUtc?: string;
    lastSignedOffDocumentDate?: string;
    providerId?: string;
    providerName?: string;
    additionalParticipantIds?: Array<GQLAdditionalParticipantFragment['id']>;
    clientPresence?: boolean;
    lastSignOffBy?: Pick<
        User,
        | 'id'
        | 'firstName'
        | 'lastName'
        | 'therapyLicense'
        | 'nationalProviderIdentifier'
    >;
    feMetadata?: {
        singleVoiceWarningAcknowledged?: boolean;
        singleVoiceWarningMistakeReported?: boolean;
        isAiClientDemoCall?: boolean;
    };
    noteTemplateIds: string[];
    errors: Array<{
        errorCode: TherapySessionErrorCode;
        errorMessage: string;
        fatal: boolean;
    }>;
};

export type TherapySessionsPagedResponse = PagedResponse<TherapySession>;

export type NamedEntityPreviewType = {
    name: string;
    entityType: NamedEntityType;
};

export type SessionProcessingProgressType = {
    detail: {
        progressNoteReady: boolean;
    };
};

export type SpeakerEvent = {
    userId: string;
    timestampMilliseconds: number;
};

export enum RecordingType {
    Audio = 'Audio',
    Video = 'Video',
}

export interface INoteInsight {
    text: GQLEditorSegment;
    input?: GQLEditorSegment;
}

export interface INoteInsights {
    category: string;
    categoryDisplayName: string;
    notes: INoteInsight[];
}

// explicitly set `undefined` as option because by default `record[userId]` returns `T`, even it technically can be undefined
type UserIdRecord<T> = Record<User['id'], T | undefined | null>;

export interface IAnalyticsSummary {
    talkingRatio: UserIdRecord<{
        ratio: number; // in normalized percents, eg. 0.53
        ratioLongTerm?: number;
        duration: number; // in milliseconds
        durationLongTerm?: number;
    }>;
    speechCadence: UserIdRecord<{
        cadence: number; // in words per minute
        cadenceLongTerm?: number;
    }>;
    responseTime: UserIdRecord<{
        time: number; // in milliseconds
        timeLongTerm?: number;
    }>;
    sentiment: UserIdRecord<{
        negative: number;
        negativeLongTerm?: number;
        positive: number;
        positiveLongTerm?: number;
    }>;
    timePerspective: UserIdRecord<{
        past: number;
        pastLongTerm?: number;
        future: number;
        futureLongTerm?: number;
    }>;
}

export type SessionAnalyticsSerieValue = {
    timestamp: number;
    value: number;
};

export type SessionAnalyticsSerie = {
    userId: User['id'];
    values: Array<SessionAnalyticsSerieValue>;
};

export type SessionAnalyticsAggregate = {
    userId: User['id'];
    value: number;
    unit: 'ms' | '%' | 'wpm';
};

export type SessionAnalyticsMetric = {
    series?: Array<SessionAnalyticsSerie> | null;
    aggregate?: Array<SessionAnalyticsAggregate> | null;
};

type SessionAnalyticsMetricType =
    | 'futureTimePerspective'
    | 'negativeSentiment'
    | 'pastTimePerspective'
    | 'positiveSentiment'
    | 'responseTime'
    | 'silentTime'
    | 'speechCadence'
    | 'talkingDuration'
    | 'talkingRatio';

export type ISessionAnalytics = Record<
    SessionAnalyticsMetricType,
    SessionAnalyticsMetric | undefined | null
>;

export enum MedicalNoteType {
    Progress = 'progress',
    Intake = 'intake',
    Soap = 'soap',
    GpSoap = 'gpSoap',
    Dap = 'dap',
    Emdr = 'emdr',
    Girp = 'girp',
    Birp = 'birp',
    Custom = 'custom',
    Mse = 'mse',
    PsySoap = 'psychiatricSoap',
    PsyIntake = 'psychiatricIntake',
    CoupleStandard = 'coupleStandard',
    CoupleSoap = 'coupleSoap',
    CoupleDap = 'coupleDap',
    HeadwaySoap = 'headwaySoap',
    Bps = 'bps',
    Template = 'template',
}

// TBD how to do type safety here without a need for MedicalNoteType and using TherapySessionType instead?
export type StaticNoteEditorType =
    | 'therapy'
    | MedicalNoteType
    | 'clientSummary';
export type NoteEditorType =
    | StaticNoteEditorType
    | GQLSessionDocumentFragment['id'];

export type TherapySessionDetail = {
    sessionId: string;
    counter?: number;
    contractId?: string;
    sessionName?: string;
    sessionType: TherapySessionType;
    state: GQLSessionState;
    recordingType?: RecordingType;
    recordingUrl?: string;
    chapters: IChapter[];
    modality: TherapeuticApproach | null;
    additionalParticipants?: GQLAdditionalParticipantFragment[];
    clientPresence?: boolean;
    transcript?: ITranscript;
    isTranscriptDeleted?: boolean;
    analyticsSummary?: IAnalyticsSummary;
    editors: { therapy: GQLEditorSegment[] } & {
        clientSummary: GQLEditorSegment[];
    } & {
        [key in MedicalNoteType]?: GQLEditorSegment[];
    };
    noteInsights: {
        [key in MedicalNoteType]?: INoteInsights[];
    };
    recordingDuration?: number;
    recordingDate?: string;
    entities: INamedEntitySummary[];
    noteTitles: {
        [key in MedicalNoteType]?: string;
    };
    lastProcessingDateUtc?: string;
};

export interface INamedEntitySummary {
    entityId: string;
    entityType: NamedEntityType;
    entityName: string;
    summary: string;
    spans: { start: number; end: number }[];
    inaccurate: boolean | null;
}

export enum NamedEntityType {
    Person = 'person_name',
    MedicalCondition = 'medical_condition',
    Organization = 'organization',
    Location = 'location',
    Drug = 'drug',
    MedicalProcess = 'medical_process',
}

export type SpeakerVoiceLetter = string; // eg. A, B, C,..

export interface ISpeakerMapping {
    [key: SpeakerVoiceLetter]: string;
}

export interface ITranscript {
    transcriptId: string;
    utterances: IUtterance[];
    speakerUserIdMapping: ISpeakerMapping;
    speakerAdditionalParticipantIdMapping: ISpeakerMapping;
}

export interface IUtterance {
    text: string;
    start: number;
    end: number;
    speaker: SpeakerVoiceLetter;
    words: IUtteranceWord[];
}

export interface IUtteranceWord {
    text: string;
    start: number;
    end: number;
}

export interface IChapter {
    chapterId: string;
    gist: GQLEditorSegment;
    headline: GQLEditorSegment;
    start: number; // start in milliseconds of recording
    end: number; // end in milliseconds of recording
    snippets: GQLEditorSegment[];
}

export type TranscriptSpeaker = {
    type: 'provider' | 'client' | 'additionalParticipant';
    color: string;
    displayName: string;
    id: string;
};

export enum TrayButtonType {
    Divider = 'Divider',
    Filler = 'Filler',
    SessionMetadata = 'session-metadata',
    MuteCam = 'camera',
    MuteMic = 'mute-mic',
    ScreenShare = 'screen',
    Leave = 'leave',
    Chat = 'chat',
    Participants = 'participants',
    BookSession = 'book_session',
    ViewOptions = 'view_options',
    NotesHistory = 'notes_history',
    Intake = 'intake',
    Assistant = 'assistant',
}

export enum KeepRecording {
    Full = 'keep-recording-full',
    AudioOnly = 'keep-recording-audio-only',
    None = 'keep-recording-none',
}

export enum TrayMenuPosition {
    Arrow = 'arrow', // show menu with arrow button
    Main = 'main', // show menu with main button
    None = 'none', // no menu is shown
}

export type ChatMessage = {
    sender: string;
    message: string;
};

/**
 * Consent types
 */

export enum ConsentInitiator {
    App = 'Upheal',
    Therapist = 'Therapist',
}

export enum ConsentDocumentType {
    PrivacyPolicy = 'PrivacyPolicy',
    TermsOfService = 'Terms',
    BAA = 'Baa',
}

export type ConsentDocument = {
    type: ConsentDocumentType;
    url: string;
};

export type ConsentPurpose = {
    id: string;
    description: string;
};

export type Consent = {
    id: string;
    initiator: ConsentInitiator;
    documents: Array<ConsentDocument>;
    purposes: Array<ConsentPurpose>;
    acceptedTimestamp?: string;
};

export type AcceptedConsent = {
    id: string;
    consentId: string;
    initiator: ConsentInitiator;
    purposes: { id: string; accepted: boolean }[];
    acceptedTimestamp?: string;
    documents: Array<ConsentDocument>;
};

export type ConsentPurposeAcceptance = {
    id: string;
    accepted: boolean;
};

export type ConsentAcceptance = {
    id: string;
    acceptedDocuments: Array<ConsentDocumentType>;
    purposes: Array<ConsentPurposeAcceptance>;
};

export type SignUpDetails = {
    userId: string;
    password: string;
    rememberMe: boolean;
    consents: Array<ConsentAcceptance>;
    firstName: string;
    lastName: string;
    verificationCode: string;
    inviteId: string;
    workPhone?: {
        countryCode: string;
        number: string;
    };
    countryCode?: string;
    hubSpotTrackingId?: string;
};

/**
 * Session Detail
 */

export enum SegmentContentType {
    PLAIN_TEXT = 'text/plain',
    PROSEMIRROR_JSON = 'text/json;schema=prosemirror',
}

export enum SourceDocumentType {
    TRANSCRIPT = 'Transcript',
    THERAPY_SESSION = 'TherapySession',
    ENTITIES = 'NamedEntity',

    // TBD: how to eliminate the need for extending this everytime a new MedicalNoteType is added
    PROGRESS_NOTE = 'ProgressNote',
    INTAKE_NOTE = 'IntakeNote',
    BPS_NOTE = 'BpsNote',
    SOAP_NOTE = 'SoapNote',
    GP_SOAP_NOTE = 'GpSoapNote',
    HEADWAY_SOAP_NOTE = 'HeadwaySoapNote',
    DAP_NOTE = 'DapNote',
    EMDR_NOTE = 'EmdrNote',
    GIRP_NOTE = 'GirpNote',
    BIRP_NOTE = 'BirpNote',
    CUSTOM_NOTE = 'CustomNote',
    MSE_NOTE = 'MseNote',
    PSY_SOAP_NOTE = 'PsychiatricSoapNote',
    PSY_INTAKE_NOTE = 'PsychiatricIntakeNote',

    CHAPTER_GIST = 'ChapterGist',
    CHAPTER_HEADLINE = 'ChapterHeadline',
    CHAPTER_SNIPPET = 'ChapterSnippet',

    COUPLE_STANDARD_NOTE = 'CoupleStandardNote',
    COUPLE_SOAP_NOTE = 'CoupleSoapNote',
    COUPLE_DAP_NOTE = 'CoupleDapNote',

    TEMPLATE = 'Template',
}

// Waiting room
export enum WaitingUserState {
    NONE = 'None',
    WAITING = 'Waiting',
    READY = 'Ready',
}

export type WaitingUser = {
    userId: User['id'];
    state: WaitingUserState;
    firstName: string;
    lastName: string;
};

export type WaitingRoom = {
    id: string;
    roomUrl: string;
    upcomingSession: {
        ownerName: string;
        session: TherapySession;
    } | null;
    waitingUser: WaitingUser;
};

// Organizations
export type Organization = {
    id: string;
    name: string;
    phoneNumber?: PhoneNumber;
    email?: string;
    billing?: {
        hasDiscount: boolean;
    };
    referredBy?: string;
    allowDataCollectionForAppImprovement?: boolean;
    address?: IAddress;
    region?: GQLOrganizationRegion;
    extras?: {
        key: string;
        value: string;
    }[];
    outputLanguage?: string;
    isUphealOS?: boolean;
};

export enum OrganizationPreferenceKey {
    SkipProviderConsent = 'SkipProviderConsentForClients',
    SessionReminders = 'SessionReminders',
    AutoDeleteSessionDelay = 'AutoDeleteSessionDelay',
    AutoDeleteTranscriptDelay = 'AutoDeleteTranscriptDelay',
    SSOEnabled = 'SSOEnabled',
    SSOProvider = 'SSOProvider',
    SSODomain = 'SSODomain',
    SharedClientsModeEnabled = 'SharedClientsModeEnabled',
    OrgAdminSharedClientsModeEnabled = 'OrgAdminSharedClientsModeEnabled',
}

export type OrganizationPreferences = {
    [OrganizationPreferenceKey.SkipProviderConsent]?: boolean;
    [OrganizationPreferenceKey.SessionReminders]?: Reminder[];
    [OrganizationPreferenceKey.AutoDeleteSessionDelay]?: string;
    [OrganizationPreferenceKey.AutoDeleteTranscriptDelay]?: string;
    [OrganizationPreferenceKey.SSOEnabled]?: boolean;
    [OrganizationPreferenceKey.SSOProvider]?: string;
    [OrganizationPreferenceKey.SSODomain]?: string;
    [OrganizationPreferenceKey.SharedClientsModeEnabled]?: boolean;
    [OrganizationPreferenceKey.OrgAdminSharedClientsModeEnabled]?: boolean;
};

// Session reminiders

export enum ReminderType {
    Email = 'Email',
}

export type Reminder = {
    name: string;
    enabled: boolean;
    timeBefore: string; // ISO duration
    type: ReminderType;
};

export enum StaticEditorTabValue {
    PRIVATE = 'Private',
    MEDICAL = 'Medical',
    CLIENT_NOTE = 'ClientNote',
    ADD_NEW = 'AddNew',
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type EditorTabValue = StaticEditorTabValue | (string & {});

export const ClientUnassignedId = '00000000-0000-0000-0000-000000000000';
export type TClientUnassignedId = typeof ClientUnassignedId;

export type ClientParticipant = Pick<
    GQLClientContact,
    'id' | 'firstName' | 'lastName'
>;

export enum TherapeuticApproach {
    COGNITIVE_BEHAVIORAL_THERAPY = 'Cognitive Behavioral Therapy',
    PSYCHOANALYTIC_THERAPY = 'Psychoanalytic Therapy',
    PSYCHODYNAMIC_THERAPY = 'Psychodynamic Therapy',
    DIALECTICAL_BEHAVIOR_THERAPY = 'Dialectical Behavior Therapy',
    GESTALT_THERAPY = 'Gestalt Therapy',
    PERSON_CENTERED_THERAPY = 'Person Centered Therapy',
    ACCEPTANCE_AND_COMMITMENT_THERAPY = 'Acceptance And Commitment Therapy',
    SYSTEMIC_THERAPY = 'Systemic Therapy',
    EXISTENTIAL_THERAPY = 'Existential Therapy',
    INTERNAL_FAMILY_SYSTEMS_THERAPY = 'Internal Family Systems Therapy',
    NARRATIVE_THERAPY = 'Narrative Therapy',
    INTEGRATIVE_THERAPY = 'Integrative Therapy',
}
